import axios from 'axios'
import appConfig from '@/config'
import authService from '@/services/support/AuthService'
import cookiesService from '@/services/support/CookiesService'
import { APIErrorFormat } from './types/Errors'
import { TOKEN_TYPE_HINT } from './types/Login'
import LogService from './services/support/LogService'
import router from '@/router'
import MESSAGES from './services/PostMessages'

// Remove the termination from the url
// apiPlatformUrl = https://{stage}.bluemessaging.net/rest/v0
const basePlatformUrl = appConfig.apiPlatformUrl.slice(0, -8)

const getRefreshToken = () => {
  return cookiesService.get(appConfig.refreshTokenCookieName)
}

/** Verify if the current URL is the login */
const alreadyInLogin = () => {
  return router.currentRoute.path === '/login'
}

/**
 * Sets the top window url to redirect at platform index
 */
const redirectToPlatformIndex = () => {
  // Remove the termination from the url
  // apiPlatformUrl = https://{stage}.bluemessaging.net/rest/v0
  const baseUrl = appConfig.apiPlatformUrl.slice(0, -8)
  const url = `${baseUrl}index.xsp`
  // We need to redirect the top location because the login is embeded
  window.top!.location.href = url
}

const sendLogoutMessage = () => {
  window.parent.postMessage(
    {
      action: MESSAGES.LOGOUT
    },
    basePlatformUrl
  )
  router.push({ name: 'ForbiddenRequest' })
}

const handleApiErrors = (error: any) => {
  LogService.error('Handling error. ' + error)
}

/**
 * Api instance
 */
const api = axios.create({
  baseURL: appConfig.apiPlatformUrl
})

let isAlreadyFetchingAccessToken = false
let subscribers: any[] = []

function onAccessTokenFetched (accessToken: any) {
  subscribers = subscribers.filter(callback => callback(accessToken))
}

function addSubscriber (callback: any) {
  subscribers.push(callback)
}

/**
 * Revoke both tokens; access and refresh
 */
async function revokeTokens () {
  await revokeAccessToken()
  await revokeRefreshToken()
}

async function revokeAccessToken () {
  const token = cookiesService.get(appConfig.jwtAccessTokenCookieName)
  if (token) {
    await authService.revokeToken(token, TOKEN_TYPE_HINT.ACCESS_TOKEN)
      .catch((error) => {
        LogService.error('Error on revoking access token. ' + error)
      })
  }
}

async function revokeRefreshToken () {
  const token = cookiesService.get(appConfig.refreshTokenCookieName)
  if (token) {
    await authService.revokeToken(token, TOKEN_TYPE_HINT.REFRESH_TOKEN)
      .catch((error) => {
        LogService.error('Error on revoking refresh token. ' + error)
      })
  }
}

function removeJWTAccessToken () {
  cookiesService.remove(appConfig.jwtAccessTokenCookieName)
}

function removeJWTRefreshToken () {
  cookiesService.remove(appConfig.refreshTokenCookieName)
}

/**
 * Removes the JWT token cookies
 */
function removeJWTValues () {
  // Remove the cookies due to its values are no longer useful
  removeJWTAccessToken()
  removeJWTRefreshToken()
}

/**
 * Validates if the current token is invalid, it means,
 * token not present, expired or it is in the black list.
 *
 * @param config - original request configuration
 * @param status - status error
 * @param apiError - error response by api
 * @returns if the token is invalid
 */
async function tokenFilter (config: any, status: any, apiError: any) {
  const error = apiError.response.data
  let invalidToken: boolean = false
  const invalidCodes = [
    1001, 2001, // Authorization is not present
    2002, // Invalidated access JWT in black list
    2602, // Invalidated refresh JWT in black list,
    2608 // Device is in resettle status
  ]

  invalidToken = error.errors.some((err: { descripcion: string, code: number }) => invalidCodes.includes(err.code))
  const originalRequest = config

  if (status === 401 && invalidToken) {
    if (!isAlreadyFetchingAccessToken) {
      isAlreadyFetchingAccessToken = true

      const refreshTokenCookie = cookiesService.get(appConfig.refreshTokenCookieName)
      const refreshToken = refreshTokenCookie && refreshTokenCookie !== 'undefined' && refreshTokenCookie !== 'null' ? refreshTokenCookie : undefined

      if (!refreshToken) {
        if (!alreadyInLogin()) sendLogoutMessage()
      } else {
        await authService
          .refreshToken(refreshToken)
          .then(tokenResponse => {
            isAlreadyFetchingAccessToken = false
            onAccessTokenFetched(tokenResponse.access_token)
          })
          .catch((e) => {
            LogService.error('Error on refresh token. ' + e)
            removeJWTValues()
            if (!alreadyInLogin()) sendLogoutMessage()
          })
      }
    }

    const retryOriginalRequest = new Promise(resolve => {
      addSubscriber((accessToken: string) => {
        originalRequest.headers.Authorization = 'Bearer ' + accessToken
        resolve(axios(originalRequest))
      })
    })
    return retryOriginalRequest
  }
  return Promise.reject(error)
}
/**
 * Validades the license status, this includes the user and device status.
 *
 * @param config - request configuration
 * @param status - error status
 * @param apiError - error response by api
 */
function licenseFilter (config: any, status: any, apiError: any) {
  const error: APIErrorFormat = APIErrorFormat.getInstance(apiError.response.data)
  let invalidLicense: boolean = false
  const invalidCodes = [
    2097, // User have updtePassword status, session must be clossed
    2010, // User doesn't have a valid status
    2601, // Invalid user status
    2603, 2015, // Associated license is not active
    2604, 2011 // Device associated with the license has status resettle
  ]
  const UserInvalidStatus = 'User doesn\'t have a valid status'
  const UserInactive = 'Invalid user status'
  const LicenseInactive = 'Associated license is not active'
  const DeviceResettle = 'Device associated with the license has status resettle'
  const DomainTemporarilyClosed = 'Domain temporarily closed'

  const invalidLicenseError = [
    UserInvalidStatus,
    UserInactive,
    LicenseInactive,
    DeviceResettle,
    DomainTemporarilyClosed
  ]

  if (error.isOldErrorFormat()) {
    invalidLicense = invalidLicenseError.includes(error.description)
  } else if (error.isNewErrorFormat()) {
    invalidLicense = error.errors.some((err) => {
      // We handle this case because there is another error description with the same error code.
      // 2016 -> Domain temporarily closed
      if (err.code === 2016 && err.description === DomainTemporarilyClosed) {
        return true
      }
      return invalidCodes.includes(err.code)
    })
  }
  if (status === 403 && invalidLicense) {
    if (!alreadyInLogin()) sendLogoutMessage()
  }
}

api.interceptors.response.use(
  response => {
    return response
  },
  error => {
    const {
      config,
      response: { status }
    } = error
    if (status === 401) {
      tokenFilter(config, status, error)
    }
    if (status === 403) {
      licenseFilter(config, status, error)
    }
    handleApiErrors(error)
    return Promise.reject(error)
  }
)

export default api
export {
  redirectToPlatformIndex,
  sendLogoutMessage,
  removeJWTValues,
  revokeTokens,
  getRefreshToken,
  removeJWTAccessToken,
  removeJWTRefreshToken
}
